import { createSlice } from "@reduxjs/toolkit";

/**
 * Markdown Optimisation drafts.
 *
 * A plan built in the MDO template builder is kept here as it is worked
 * on, so it shows up on Templates → MDO without being saved by hand,
 * and can be resumed later with everything filled back in. Two routes
 * read the same list — the builder writes it, the tab lists it — which
 * is why it lives in the store rather than in either component.
 *
 * Client-side only for now: when the plans endpoint lands, this slice
 * becomes the optimistic layer over it rather than the source.
 *
 * A draft is:
 *   id        string   stable key, minted by the builder
 *   name      string   template name
 *   status    'DRAFT' | 'READY' | 'COMPLETE'
 *   savedAt   string   ISO, bumped on every write
 *   plan      { seasons, events, buckets, seasonId, eventId, bucketId }
 *   commitment object?  set once the run is committed
 */
const initialState = {
  drafts: [],
};

const mdoDraftsSlice = createSlice({
  name: "mdoDrafts",
  initialState,
  reducers: {
    // Upsert: the builder writes the whole draft on every change, so
    // resuming later replays exactly what was on screen.
    mdoDraftSaved(state, action) {
      const draft = action.payload;
      if (!draft?.id) return;
      const at = state.drafts.findIndex((d) => d.id === draft.id);
      const next = { ...draft, savedAt: draft.savedAt ?? new Date().toISOString() };
      if (at === -1) state.drafts.unshift(next);
      else state.drafts[at] = next;
    },
    mdoDraftDiscarded(state, action) {
      state.drafts = state.drafts.filter((d) => d.id !== action.payload);
    },
    mdoDraftsReset() {
      return initialState;
    },
  },
});

export const { mdoDraftSaved, mdoDraftDiscarded, mdoDraftsReset } =
  mdoDraftsSlice.actions;

// Defensive reads — a consumer mounted in a lighter test store without
// this reducer would otherwise crash on ``state.mdoDrafts``.
export const selectMdoDrafts = (state) => state.mdoDrafts?.drafts ?? [];
export const selectMdoDraft = (id) => (state) =>
  selectMdoDrafts(state).find((d) => d.id === id) ?? null;

export default mdoDraftsSlice.reducer;
